import { type FC, useMemo } from "react";

import RequestFeedbackPopupForm from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/components/v2/RequestFeedbackPopupForm";
import { useForm } from "react-hook-form";
import { Button, Dialog, DialogActions, DialogContent, DialogHeader, Stack, Typography } from "~/common/mui";
import { useTranslation } from "react-i18next";
import {
  type FeedbackOptionCategory,
  useOrderListFeedback,
  type UserData,
} from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/components/OrderListFeedbackContext";
import type { OrderData, OrderLineItemData } from "~/domains/orders/types/modelsV2";
import type { MultilevelSelectItemItem } from "~/common/components/Select/MultilevelSelect";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { Col, Row } from "~/common/components/Layout";
import { ParamComponent } from "~/common/components/ParamsGrid";
import { apiSendRequest } from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/api/SendRequestFeedback";
import { CreateCaseSource } from "~/features/orders/orderDetails/components/ServiceCases/types";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  userData: UserData;
  orderFeedbackOptions: FeedbackOptionCategory[];
  soNumber?: string;
  poNumber?: string;
  orderDetailsEntryData: OrderLineItemData;
  orderData: OrderData;
  lineItemIndex: number;
  subLineNo?: string;
  source?: CreateCaseSource;
}

const RequestFeedbackPopup: FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const { onClose, onSuccess, subLineNo, source } = props;
  const orderDetailsEntryData = props.orderDetailsEntryData;
  const soNumber = props.soNumber;
  const poNumber = props.poNumber;
  const { userData } = useOrderListFeedback();

  const { register, control, getValues, handleSubmit, formState } = useForm();

  const dropdownOptions: MultilevelSelectItemItem[] = useMemo(() => {
    return props.orderFeedbackOptions.map((item) => ({
      label: t(item.label),
      value: item.value,
      title: item.title,
      children: item.children.map((child) => ({
        label: t(child.label),
        value: child.value,
        parentValue: item.value,
      })),
    }));
  }, [t, t, props.orderFeedbackOptions]);

  const sendFeedback = () => {
    const translationsMap: Dict<string> = {};

    dropdownOptions.forEach((category) => {
      if (category.children) {
        category.children.forEach((child) => {
          translationsMap[child.value] = category.label + " - " + child.label;
        });
      }
    });

    const formValues = getValues();
    if (soNumber && poNumber) {
      const packagingLabel = getPackagingLabelAtLineItem({
        t: t,
        productDescriptor: fakeDescriptorFromProduct(orderDetailsEntryData.product),
        packCode: orderDetailsEntryData.packCode,
        packSize: orderDetailsEntryData.packSize,
        quantity: subLineNo
          ? orderDetailsEntryData.subEntries.find((subEntry) => subEntry.lineNo === subLineNo)?.confirmedQty
          : orderDetailsEntryData.qty,
      }) as string;

      apiSendRequest({
        comment: formValues.comment,
        subjectOfRequest: translationsMap[formValues.subject],
        subjectCode: formValues.subject,
        soNumber: soNumber,
        poNumber: poNumber,
        orderDetailsEntryData: orderDetailsEntryData,
        packagingLabel: packagingLabel,
        salesOrg: props.orderData.account?.salesOrg ?? undefined,
        accountNumber: props.orderData.account?.code ?? undefined,
        subLineNo: subLineNo,
      }).then((result) => {
        if (result) {
          onSuccess();
        }
      });
    }
  };

  return (
    <Dialog open={true} maxWidth="desktop-s">
      <DialogHeader onClose={onClose}>
        <Typography variant="headlineSmall">{t("serviceCases.addCase.header")}</Typography>
      </DialogHeader>
      <DialogContent>
        <Stack gap={4}>
          <Stack gap={2}>
            <Row>
              <Col size={4}>
                <ParamComponent
                  label={t("request.information.order.line")}
                  value={subLineNo ?? orderDetailsEntryData.lineNo}
                />
              </Col>
              <Col size={4}>
                <ParamComponent label={t("request.information.sales.order")} value={soNumber} />
              </Col>
            </Row>
            <Row>
              <Col size={4}>
                <ParamComponent
                  label={t("request.information.name")}
                  value={`${userData.firstName} ${userData.lastName}`}
                />
              </Col>
              <Col size={4}>
                <ParamComponent label={t("request.information.email")} value={userData.email} />
              </Col>
              <Col size={4}>
                <ParamComponent label={t("request.information.phone")} value={userData.phone ? userData.phone : "-"} />
              </Col>
            </Row>
          </Stack>
          <RequestFeedbackPopupForm register={register} control={control} dropdownOptions={dropdownOptions} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction={"row"} gap={1}>
          <Button size={"medium"} variant={"text"} onClick={onClose}>
            {source == CreateCaseSource.LIST_PAGE ? t("serviceCases.list.dialog.viewCaseButton") : t("cancel")}
          </Button>
          <Button disabled={!formState.isValid} onClick={handleSubmit(sendFeedback)}>
            {t("serviceCases.button.create")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default RequestFeedbackPopup;
